// Offentlig driftsstatus på /status. Tegnes uten innlogging, og denne modulen
// skal aldri importere auth.ts, config.ts eller ui.ts: siden må virke når
// id.actiwise.no er nede, for det er da brukerne leter etter den.
//
// Dataene er /status.json fra samme origin. CloudFront henter den fra
// actiwise-observability sin bøtte (se terraform/cloudfront.tf), så CSP-en
// (connect-src 'self') trenger ingen ny kilde. Formen er avtalt med
// observability; se README, "Driftsstatus".
import logoUrl from "@actiwise/brand/assets/logo-horizontal-360.png";
import { el, rot } from "./dom";

export const STATUS_STI = "/status";

const DATA_URL = "/status.json";
const OPPDATER_HVERT_MS = 60_000;
const TIDSAVBRUDD_MS = 10_000;
// Lambdaen skriver hvert 5. minutt. Tre bommer på rad er ikke lenger
// forsinkelse, og da skal brukeren vite at bildet kan være gammelt.
const FOR_GAMMEL_MS = 15 * 60_000;
const MAKS_DAGER = 90;
// Under 768 px blir 90 stolper for smale til å se. Da vises de siste 30;
// grensen må stemme med .dag:nth-last-child i style.css.
const DAGER_MOBIL = 30;

// --- Kontrakten med actiwise-observability ---

type AppStatus = "up" | "down" | "unknown";
type DagNivaa = "ok" | "redusert" | "nede" | "ingen";

interface Dag {
  dato: string;
  oppetid: number | null;
}

interface Tjeneste {
  navn: string;
  status: AppStatus;
  oppetid90: number | null;
  dager: Dag[];
}

interface Driftsstatus {
  generert: Date;
  tjenester: Tjeneste[];
}

/** status.json har en form vi ikke kjenner. */
class FormFeil extends Error {}
/** status.json kunne ikke hentes i det hele tatt. */
class HenteFeil extends Error {}

function erObjekt(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function prosentEllerNull(v: unknown, hva: string): number | null {
  if (v === null) return null;
  if (typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 100) {
    return v;
  }
  throw new FormFeil(`${hva} er verken en prosent eller null`);
}

function tolkDag(v: unknown, hva: string): Dag {
  if (!erObjekt(v)) throw new FormFeil(`${hva} er ikke et objekt`);
  const { date, uptime } = v;
  if (typeof date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    throw new FormFeil(`${hva}.date er ikke en dato (YYYY-MM-DD)`);
  }
  return { dato: date, oppetid: prosentEllerNull(uptime, `${hva}.uptime`) };
}

function tolkTjeneste(v: unknown, i: number, spraak: Spraak): Tjeneste {
  const hva = `apps[${i}]`;
  if (!erObjekt(v)) throw new FormFeil(`${hva} er ikke et objekt`);
  const { id, name, status, uptime_90d, days } = v;

  if (typeof id !== "string") throw new FormFeil(`${hva}.id mangler`);
  if (!erObjekt(name) || typeof name.no !== "string") {
    throw new FormFeil(`${hva}.name.no mangler`);
  }
  // Mangler den engelske teksten, er det norske navnet bedre enn ingenting.
  const lokalt = name[spraak];
  const navn = typeof lokalt === "string" ? lokalt : name.no;

  if (status !== "up" && status !== "down" && status !== "unknown") {
    throw new FormFeil(`${hva}.status er ikke up, down eller unknown`);
  }
  if (!Array.isArray(days)) throw new FormFeil(`${hva}.days er ikke en liste`);

  return {
    navn,
    status,
    oppetid90: prosentEllerNull(uptime_90d, `${hva}.uptime_90d`),
    // Eldste først. Skulle det komme flere enn 90, er det de nyeste som teller.
    dager: days.slice(-MAKS_DAGER).map((d, j) => tolkDag(d, `${hva}.days[${j}]`)),
  };
}

/**
 * Validerer status.json defensivt. Fila kommer fra et annet repo og en annen
 * deploy; en ukjent `schema` eller feil form skal gi en forståelig melding,
 * ikke en halvtegnet side eller et kast midt i DOM-byggingen.
 */
function tolk(data: unknown, spraak: Spraak): Driftsstatus {
  if (!erObjekt(data)) throw new FormFeil("roten er ikke et objekt");
  if (data.schema !== 1) {
    throw new FormFeil(`ukjent schema: ${JSON.stringify(data.schema)}`);
  }
  const generert =
    typeof data.generated_at === "string" ? new Date(data.generated_at) : null;
  if (!generert || Number.isNaN(generert.getTime())) {
    throw new FormFeil("generated_at er ikke et tidspunkt");
  }
  if (!Array.isArray(data.apps)) throw new FormFeil("apps er ikke en liste");

  return {
    generert,
    tjenester: data.apps.map((a, i) => tolkTjeneste(a, i, spraak)),
  };
}

async function hent(spraak: Spraak): Promise<Driftsstatus> {
  let svar: Response;
  try {
    // no-cache: revalider alltid. Uten den kan nettleseren gi oss sin egen
    // kopi i opptil max-age, og vi ville vist samme minutt to ganger.
    svar = await fetch(DATA_URL, {
      cache: "no-cache",
      headers: { Accept: "application/json" },
      signal: AbortSignal.timeout(TIDSAVBRUDD_MS),
    });
  } catch (feil) {
    throw new HenteFeil(String(feil));
  }
  if (!svar.ok) throw new HenteFeil(`HTTP ${svar.status}`);

  // Finnes ikke fila i bøtta, svarer S3 403, og CloudFronts SPA-fallback gjør
  // det om til index.html med 200. Det er "ikke publisert", ikke "feil form".
  const type = svar.headers.get("content-type") ?? "";
  if (!type.includes("json")) {
    throw new HenteFeil(`fikk ${type || "ukjent innholdstype"}, ikke JSON`);
  }

  let data: unknown;
  try {
    data = await svar.json();
  } catch {
    throw new FormFeil("svaret er ikke gyldig JSON");
  }
  return tolk(data, spraak);
}

// --- Tekster ---
//
// Portalen er norsk. ?lang=en finnes fordi den engelske siden på
// www.actiwise.no lenker hit, og en engelsk leser skal ikke møte en norsk
// statusside.

type Spraak = "no" | "en";

const TEKSTER = {
  no: {
    locale: "nb-NO",
    tittel: "Driftsstatus",
    dokumenttittel: "Driftsstatus – ActiWise",
    tilPortalen: "Til portalen",
    laster: "Henter driftsstatus …",
    alleOppe: "Alle tjenester er i drift",
    noenNede: "Noen tjenester har problemer",
    noenUkjent: "Status for noen tjenester er ukjent",
    ingenTjenester: "Ingen tjenester å vise ennå",
    status: { up: "I drift", down: "Nede", unknown: "Ukjent" },
    oppsummering: (oppetid: string | null, reduserte: string | null) =>
      `Siste 90 dager: ${oppetid === null ? "oppetid ukjent" : `${oppetid} oppetid`}` +
      (reduserte === null ? "" : `, ${reduserte}`),
    reduserteDager: (n: number) =>
      n === 0
        ? "ingen dager med redusert drift"
        : `${n} ${n === 1 ? "dag" : "dager"} med redusert drift`,
    dagerEtikett: (navn: string) => `${navn}: tilgjengelighet per dag`,
    dag: (dato: string, p: string | null) =>
      p === null ? `${dato}: ingen data` : `${dato}: ${p} oppetid`,
    dagerSiden: (n: number) => `${n} dager siden`,
    iDag: "I dag",
    forklaring: "Forklaring",
    nivaa: {
      ok: "I drift",
      redusert: "Redusert",
      nede: "Nede",
      ingen: "Ingen data",
    },
    sistOppdatert: (tid: string) => `Sist oppdatert ${tid}`,
    gammel: (tid: string) =>
      `Statusen er ikke oppdatert siden ${tid}. Det som vises kan være utdatert.`,
    henteFeil: "Klarte ikke å hente driftsstatus",
    henteFeilDetalj:
      "Statustjenesten svarte ikke. Prøv igjen om litt – siden prøver også selv hvert minutt.",
    formFeil: "Driftsstatusen kunne ikke leses",
    formFeilDetalj:
      "Dataene har et format denne siden ikke kjenner. Last siden på nytt; hjelper ikke det, er feilen hos oss.",
    oppdateringFeilet: (tid: string) =>
      `Klarte ikke å hente ny status. Viser det som var gjeldende ${tid}.`,
    provIgjen: "Prøv igjen",
  },
  en: {
    locale: "en-GB",
    tittel: "Service status",
    dokumenttittel: "Service status – ActiWise",
    tilPortalen: "Go to the portal",
    laster: "Loading service status …",
    alleOppe: "All services are operational",
    noenNede: "Some services are having problems",
    noenUkjent: "The status of some services is unknown",
    ingenTjenester: "No services to show yet",
    status: { up: "Operational", down: "Down", unknown: "Unknown" },
    oppsummering: (oppetid: string | null, reduserte: string | null) =>
      `Last 90 days: ${oppetid === null ? "uptime unknown" : `${oppetid} uptime`}` +
      (reduserte === null ? "" : `, ${reduserte}`),
    reduserteDager: (n: number) =>
      n === 0
        ? "no days with degraded service"
        : `${n} ${n === 1 ? "day" : "days"} with degraded service`,
    dagerEtikett: (navn: string) => `${navn}: availability per day`,
    dag: (dato: string, p: string | null) =>
      p === null ? `${dato}: no data` : `${dato}: ${p} uptime`,
    dagerSiden: (n: number) => `${n} days ago`,
    iDag: "Today",
    forklaring: "Legend",
    nivaa: {
      ok: "Operational",
      redusert: "Degraded",
      nede: "Down",
      ingen: "No data",
    },
    sistOppdatert: (tid: string) => `Last updated ${tid}`,
    gammel: (tid: string) =>
      `The status has not been updated since ${tid}. What you see may be out of date.`,
    henteFeil: "Could not load the service status",
    henteFeilDetalj:
      "The status service did not respond. Try again shortly – the page also retries every minute.",
    formFeil: "The service status could not be read",
    formFeilDetalj:
      "The data is in a format this page does not recognise. Reload the page; if that does not help, the fault is on our side.",
    oppdateringFeilet: (tid: string) =>
      `Could not load a new status. Showing the status as of ${tid}.`,
    provIgjen: "Try again",
  },
} as const;

type Tekster = (typeof TEKSTER)[Spraak];

function spraakFraUrl(): Spraak {
  return new URLSearchParams(window.location.search).get("lang") === "en"
    ? "en"
    : "no";
}

// --- Formatering ---

function prosent(t: Tekster, verdi: number): string {
  return new Intl.NumberFormat(t.locale, {
    style: "percent",
    maximumFractionDigits: 2,
  }).format(verdi / 100);
}

function tidspunkt(t: Tekster, dato: Date): string {
  return new Intl.DateTimeFormat(t.locale, {
    dateStyle: "long",
    timeStyle: "short",
  }).format(dato);
}

/** "2026-10-19" som kalenderdato. UTC, så datoen ikke flytter seg en dag
 *  for en leser vest for Greenwich. */
function kalenderdato(t: Tekster, iso: string): string {
  return new Intl.DateTimeFormat(t.locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${iso}T00:00:00Z`));
}

/**
 * Terskler for fargen på en dagsstolpe. Et minutt nede er 99,93 % av et døgn,
 * så 99,9 skiller "en enkelt bom" fra "noe skjedde". Under 95 % (over en time
 * nede) er dagen rød.
 */
function nivaa(oppetid: number | null): DagNivaa {
  if (oppetid === null) return "ingen";
  if (oppetid >= 99.9) return "ok";
  if (oppetid >= 95) return "redusert";
  return "nede";
}

// --- Tegning ---

type Tilstand =
  | { type: "laster" }
  | { type: "ok"; data: Driftsstatus }
  | { type: "feil"; feil: unknown; forrige: Driftsstatus | null };

function samletLinje(t: Tekster, tjenester: Tjeneste[]): HTMLElement {
  let tekst: string = t.alleOppe;
  let klasse = "samlet ok";
  if (tjenester.length === 0) {
    tekst = t.ingenTjenester;
    klasse = "samlet ukjent";
  } else if (tjenester.some((s) => s.status === "down")) {
    tekst = t.noenNede;
    klasse = "samlet nede";
  } else if (tjenester.some((s) => s.status === "unknown")) {
    tekst = t.noenUkjent;
    klasse = "samlet ukjent";
  }
  return el("p", klasse, tekst);
}

function dagstolper(t: Tekster, tjeneste: Tjeneste): HTMLElement {
  const liste = el("ol", "dager");
  liste.setAttribute("aria-label", t.dagerEtikett(tjeneste.navn));
  for (const dag of tjeneste.dager) {
    const tekst = t.dag(
      kalenderdato(t, dag.dato),
      dag.oppetid === null ? null : prosent(t, dag.oppetid),
    );
    const stolpe = el("li", `dag ${nivaa(dag.oppetid)}`);
    // title for mus, den skjulte teksten for skjermleser. Fargen alene sier
    // ingenting en skjermleser kan bruke.
    stolpe.title = tekst;
    stolpe.append(el("span", "skjult", tekst));
    liste.append(stolpe);
  }
  return liste;
}

function tjenesteRad(t: Tekster, tjeneste: Tjeneste): HTMLElement {
  const rad = el("li", "tjeneste");

  const topp = el("div", "tjeneste-topp");
  const merkeKlasse = { up: "i-drift", down: "nede", unknown: "ukjent" }[
    tjeneste.status
  ];
  topp.append(
    el("h2", "tjeneste-navn", tjeneste.navn),
    el("span", `status ${merkeKlasse}`, t.status[tjeneste.status]),
  );

  const n = tjeneste.dager.length;
  const harData = tjeneste.dager.some((d) => d.oppetid !== null);
  const reduserte = tjeneste.dager.filter((d) => {
    const niv = nivaa(d.oppetid);
    return niv === "redusert" || niv === "nede";
  }).length;

  const akse = el("div", "tjeneste-akse dempet liten");
  akse.setAttribute("aria-hidden", "true");
  const fra = el("span", "fra");
  if (n > DAGER_MOBIL) {
    fra.append(
      el("span", "fra-lang", t.dagerSiden(n)),
      el("span", "fra-kort", t.dagerSiden(DAGER_MOBIL)),
    );
  } else {
    fra.textContent = t.dagerSiden(n);
  }
  akse.append(fra, el("span", undefined, t.iDag));

  const oppsummering = el(
    "p",
    "oppsummering dempet liten",
    t.oppsummering(
      tjeneste.oppetid90 === null ? null : prosent(t, tjeneste.oppetid90),
      // Ingen målinger i det hele tatt: "ingen dager med redusert drift"
      // ville vært sant, men villedende.
      harData ? t.reduserteDager(reduserte) : null,
    ),
  );

  rad.append(topp);
  if (n > 0) rad.append(dagstolper(t, tjeneste), akse);
  rad.append(oppsummering);
  return rad;
}

function forklaring(t: Tekster): HTMLElement {
  const boks = el("div", "forklaring dempet liten");
  boks.append(el("span", undefined, `${t.forklaring}:`));
  for (const niv of ["ok", "redusert", "nede", "ingen"] as const) {
    const punkt = el("span", "forklaring-punkt");
    const prove = el("span", `dag ${niv}`);
    prove.setAttribute("aria-hidden", "true");
    punkt.append(prove, el("span", undefined, t.nivaa[niv]));
    boks.append(punkt);
  }
  return boks;
}

function feilkort(t: Tekster, feil: unknown, provIgjen: () => void): HTMLElement {
  const form = feil instanceof FormFeil;
  const kort = el("div", "kort");
  kort.setAttribute("role", "alert");
  kort.append(
    el("h2", undefined, form ? t.formFeil : t.henteFeil),
    el("p", "dempet", form ? t.formFeilDetalj : t.henteFeilDetalj),
  );
  const knapp = el("button", "primaer", t.provIgjen);
  knapp.type = "button";
  knapp.addEventListener("click", provIgjen);
  kort.append(knapp);
  return kort;
}

function statusVisning(t: Tekster, data: Driftsstatus): HTMLElement[] {
  const noder: HTMLElement[] = [samletLinje(t, data.tjenester)];

  const tid = tidspunkt(t, data.generert);
  if (Date.now() - data.generert.getTime() > FOR_GAMMEL_MS) {
    noder.push(el("p", "varsel", t.gammel(tid)));
  }

  if (data.tjenester.length > 0) {
    const liste = el("ul", "tjenester");
    for (const tjeneste of data.tjenester) liste.append(tjenesteRad(t, tjeneste));
    noder.push(liste, forklaring(t));
  }

  noder.push(el("p", "sist-oppdatert dempet liten", t.sistOppdatert(tid)));
  return noder;
}

function tegn(
  t: Tekster,
  innhold: HTMLElement,
  tilstand: Tilstand,
  provIgjen: () => void,
): void {
  switch (tilstand.type) {
    case "laster": {
      const boks = el("div", "laster");
      const snurr = el("div", "spinner");
      snurr.setAttribute("aria-hidden", "true");
      const tekst = el("p", undefined, t.laster);
      tekst.setAttribute("role", "status");
      boks.append(snurr, tekst);
      innhold.replaceChildren(boks);
      return;
    }
    case "ok":
      innhold.replaceChildren(...statusVisning(t, tilstand.data));
      return;
    case "feil": {
      console.warn("Driftsstatus:", tilstand.feil);
      // Har vi vist noe før, beholder vi det og sier fra over - en tom side
      // fordi ett av seksti kall i timen feilet, hjelper ingen.
      const forrige = tilstand.forrige;
      if (forrige && !(tilstand.feil instanceof FormFeil)) {
        const varsel = el(
          "p",
          "varsel",
          t.oppdateringFeilet(tidspunkt(t, forrige.generert)),
        );
        varsel.setAttribute("role", "status");
        innhold.replaceChildren(varsel, ...statusVisning(t, forrige));
      } else {
        innhold.replaceChildren(feilkort(t, tilstand.feil, provIgjen));
      }
      return;
    }
  }
}

/** Tegner /status og holder den oppdatert mens fanen er synlig. */
export function visStatusside(): void {
  const spraak = spraakFraUrl();
  const t = TEKSTER[spraak];
  document.documentElement.lang = spraak;
  document.title = t.dokumenttittel;

  const node = rot();
  const topp = el("header", "topp");
  const logo = el("img", "logo");
  logo.src = logoUrl;
  logo.alt = "ActiWise";
  const tilPortalen = el("a", "sekundaer", t.tilPortalen);
  tilPortalen.href = "/";
  topp.append(logo, tilPortalen);

  const main = el("main", "statusside");
  const innhold = el("div", "statusinnhold");
  main.append(el("h1", undefined, t.tittel), innhold);
  node.append(topp, main);

  let siste: Driftsstatus | null = null;
  let sistHentet = 0;
  let paagaar = false;
  let timer: number | undefined;

  function planlegg(om = OPPDATER_HVERT_MS): void {
    window.clearTimeout(timer);
    if (document.visibilityState === "visible") {
      timer = window.setTimeout(() => void oppdater(), om);
    }
  }

  async function oppdater(): Promise<void> {
    if (paagaar) return;
    paagaar = true;
    window.clearTimeout(timer);
    sistHentet = Date.now();
    try {
      siste = await hent(spraak);
      tegn(t, innhold, { type: "ok", data: siste }, provIgjen);
    } catch (feil) {
      tegn(t, innhold, { type: "feil", feil, forrige: siste }, provIgjen);
    } finally {
      paagaar = false;
      planlegg();
    }
  }

  function provIgjen(): void {
    tegn(t, innhold, { type: "laster" }, provIgjen);
    void oppdater();
  }

  // Skjult fane: ingen henting. Synlig igjen: hent med en gang hvis det er
  // over et minutt siden sist, ellers fortsett der vi slapp.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") {
      window.clearTimeout(timer);
      return;
    }
    const igjen = OPPDATER_HVERT_MS - (Date.now() - sistHentet);
    if (igjen <= 0) void oppdater();
    else planlegg(igjen);
  });

  provIgjen();
}
